import { countPendingMakeupSessions } from "./makeup-backlog.js";
import { isCoachScheduleCommittedSession } from "./parent-portal-booking-coach-schedule.js";
import { listParentBookingTrainerAttentionSessions } from "./parent-portal-booking-overlap.js";
import { isCancelledSession, isSessionCompleted } from "./session-status.js";
import {
  isSessionTimeEnded,
  parseSessionStartMs,
  sessionEndMs,
  toLocalDateInputValue,
} from "./session-timing.js";

function __sessionHours(session) {
  return (Number(session?.duration_min) || 0) / 60;
}

function __weekBounds(now = new Date()) {
  const monday = new Date(now);
  const dayIdx = (monday.getDay() + 6) % 7;
  monday.setDate(monday.getDate() - dayIdx);
  const sunday = new Date(monday);
  sunday.setDate(sunday.getDate() + 6);
  return {
    mondayIso: toLocalDateInputValue(monday),
    sundayIso: toLocalDateInputValue(sunday),
    todayIso: toLocalDateInputValue(now),
  };
}

/**
 * Hub-KPIs für TrainingsDesk (Web-Hub + Native iOS).
 *
 * @param {{ sessions?: object[], billingStatements?: object[], now?: Date }} input
 */
export function computeTrainingsDeskHubMetrics(input = {}) {
  const sessions = Array.isArray(input.sessions) ? input.sessions : [];
  const billingStatements = Array.isArray(input.billingStatements)
    ? input.billingStatements
    : [];
  const now = input.now instanceof Date ? input.now : new Date();
  const { mondayIso, sundayIso, todayIso } = __weekBounds(now);

  const isSessionThisWeek = (s) => {
    const d = String(s?.date || "").slice(0, 10);
    return d >= mondayIso && d <= sundayIso;
  };

  const thisWeekSessions = sessions.filter(isSessionThisWeek);
  const thisWeekCommittedSessions = thisWeekSessions.filter(
    isCoachScheduleCommittedSession,
  );
  const thisWeekCompletedHours = thisWeekCommittedSessions
    .filter((s) => Boolean(s?.attendance_confirmed) || isSessionCompleted(s))
    .reduce((sum, s) => sum + __sessionHours(s), 0);
  const thisWeekPlannedRemainingHours = thisWeekCommittedSessions
    .filter((s) => !s?.attendance_confirmed && !isSessionCompleted(s))
    .reduce((sum, s) => sum + __sessionHours(s), 0);

  const openStatements = billingStatements.filter((s) => {
    const st = String(s?.status || "");
    if (st !== "sent" && st !== "partially_paid") return false;
    return (Number(s?.open_eur) || 0) > 0.004;
  });
  const openStatementsTotalEur =
    Math.round(
      openStatements.reduce((sum, r) => sum + (Number(r.open_eur) || 0), 0) *
        100,
    ) / 100;

  const nowMs = now.getTime();
  const nextSession =
    sessions
      .filter((s) => {
        if (!isCoachScheduleCommittedSession(s)) return false;
        const end = sessionEndMs(s);
        return Number.isFinite(end) && end > nowMs;
      })
      .sort(
        (a, b) => (parseSessionStartMs(a) || 0) - (parseSessionStartMs(b) || 0),
      )[0] || null;

  const backlogCount = sessions.filter((s) => {
    if (s?.attendance_confirmed) return false;
    if (isCancelledSession(s)) return false;
    const d = String(s?.date || "").slice(0, 10);
    if (!d) return false;
    if (d < todayIso) return true;
    if (d > todayIso) return false;
    return isSessionTimeEnded(s, nowMs);
  }).length;

  const pendingMakeupCount = countPendingMakeupSessions(sessions);
  const parentBookingAttentionCount = listParentBookingTrainerAttentionSessions(
    sessions,
    { now },
  ).open.length;

  return {
    thisWeekSessionCount: thisWeekCommittedSessions.length,
    thisWeekCompletedHours,
    thisWeekPlannedRemainingHours,
    openStatementsCount: openStatements.length,
    openStatementsTotalEur,
    hasAnyStatements: billingStatements.length > 0,
    nextSession,
    backlogCount,
    pendingMakeupCount,
    parentBookingAttentionCount,
  };
}

/**
 * Sessions, die noch bestätigt werden müssen (wie Hub-Backlog).
 *
 * @param {object[]} sessions
 * @param {Date} [now]
 * @returns {object[]}
 */
export function getTrainingsDeskBacklogSessions(sessions, now = new Date()) {
  const list = Array.isArray(sessions) ? sessions : [];
  const todayIso = toLocalDateInputValue(now);
  const nowMs = now.getTime();

  return list
    .filter((s) => {
      if (s?.attendance_confirmed) return false;
      const pbs = String(s?.parent_booking_status || "").trim();
      if (
        pbs === "pending_trainer" ||
        pbs === "missed_chance" ||
        pbs === "pending_student"
      )
        return false;
      if (isCancelledSession(s)) return false;
      const d = String(s?.date || "").slice(0, 10);
      if (!d) return false;
      if (d < todayIso) return true;
      if (d > todayIso) return false;
      return isSessionTimeEnded(s, nowMs);
    })
    .sort(
      (a, b) => (parseSessionStartMs(b) || 0) - (parseSessionStartMs(a) || 0),
    );
}
